import { useState, useEffect, useCallback, useRef } from 'react';
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Typography,
  Box,
  Divider,
  CircularProgress,
  Chip,
  Button
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import SitesService from '../../services/SitesService';
import SitesOrderService from '../../services/SitesOrderService';
import { getLocations } from '../../services/LocationService';
import { fetchInvoiceTickets } from '../../services/SalesService';
import { getSitesOrdersPrintConfig } from '../../services/SettingsService';
import { useNotificationsWebSocket } from '../../hooks/useNotificationsWebSocket';
import PlatformBridge from '../../utils/PlatformBridge';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/es';

dayjs.extend(relativeTime);
dayjs.locale('es');

const playNotificationSound = async () => {
  try {
    await PlatformBridge.playNotificationSound();
  } catch (e) {
    console.warn('No se pudo reproducir el sonido de notificación:', e);
  }
};

// Aviso de conteo de stock: se calcula en el backend (no se guarda ni se marca como leído) y se muestra mientras la
// sucursal tenga conteo pendiente hoy. Se arma con la misma forma que una notificación para dibujarlo igual.
const stockCountToNotification = (item) => ({
  id: `stock-count-${item.locationId}`,
  type: 'stock_count',
  isRead: false,
  locationId: item.locationId,
  title: 'Conteo de stock',
  description: item.status === 'PENDING' && item.pendingCount > 0
    ? `Conteo de stock: quedan ${item.pendingCount} ítem${item.pendingCount === 1 ? '' : 's'} por contar en ${item.locationName}`
    : `Conteo de stock: hoy toca contar en ${item.locationName}`,
  createdAt: null
});

const NotificationsDropdown = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const open = Boolean(anchorEl);
  const prevUnreadCountRef = useRef(null);
  const isFirstLoadRef = useRef(true);
  const printedOrdersRef = useRef(new Map());
  const accessibleLocationIdsRef = useRef(null);
  const accessibleLocationIdsPromiseRef = useRef(null);

  const loadAccessibleLocationIds = useCallback(async () => {
    if (accessibleLocationIdsRef.current) {
      return accessibleLocationIdsRef.current;
    }
    if (!accessibleLocationIdsPromiseRef.current) {
      accessibleLocationIdsPromiseRef.current = getLocations()
        .then((locationsData) => {
          const ids = new Set(
            (Array.isArray(locationsData) ? locationsData : [])
              .map((location) => Number(location?.id))
              .filter((id) => Number.isFinite(id))
          );
          accessibleLocationIdsRef.current = ids;
          return ids;
        })
        .catch((error) => {
          console.warn('No se pudo cargar el alcance de sucursales para notificaciones:', error);
          accessibleLocationIdsRef.current = new Set();
          return accessibleLocationIdsRef.current;
        })
        .finally(() => {
          accessibleLocationIdsPromiseRef.current = null;
        });
    }
    return accessibleLocationIdsPromiseRef.current;
  }, []);

  const canHandleNotification = useCallback(async (notificationData) => {
    if (notificationData?.type !== 'order') return true;

    const rawLocationId = notificationData.locationId ?? notificationData.location_id ?? notificationData.branchId;
    if (rawLocationId === null || rawLocationId === undefined || rawLocationId === '') {
      return true;
    }

    const locationId = Number(rawLocationId);
    if (!Number.isFinite(locationId)) return true;

    const accessibleLocationIds = await loadAccessibleLocationIds();
    return accessibleLocationIds.size === 0 || accessibleLocationIds.has(locationId);
  }, [loadAccessibleLocationIds]);

  const printBase64 = useCallback(async (base64, viewPrint, autoPrint) => {
    if (!base64 || (!viewPrint && !autoPrint)) return;
    let pdfUrl = null;
    try {
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i += 1) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      pdfUrl = window.URL.createObjectURL(blob);

      if (viewPrint) {
        window.open(pdfUrl, '_blank');
      }
      if (autoPrint) {
        const printedNatively = await PlatformBridge.printPdf(base64);
        if (!printedNatively && !viewPrint) {
          window.open(pdfUrl, '_blank');
        }
      }
    } finally {
      if (pdfUrl) {
        setTimeout(() => {
          try {
            window.URL.revokeObjectURL(pdfUrl);
          } catch (_) {
            // no-op
          }
        }, 60_000);
      }
    }
  }, []);

  const autoPrintOrderTickets = useCallback(async (notificationData) => {
    if (!notificationData || notificationData.type !== 'order') return;
    const orderId = Number(notificationData.orderId);
    if (!orderId || Number.isNaN(orderId)) return;
    console.info('[AutoPrintOrder] Evento recibido', { orderId, notificationData });

    const now = Date.now();
    const existing = printedOrdersRef.current.get(orderId);
    if (existing && now - existing < 90_000) {
      console.info('[AutoPrintOrder] Saltado por dedupe', { orderId, elapsedMs: now - existing });
      return;
    }

    printedOrdersRef.current.set(orderId, now);
    for (const [key, value] of printedOrdersRef.current.entries()) {
      if (now - value > 30 * 60 * 1000) {
        printedOrdersRef.current.delete(key);
      }
    }

    try {
      const order = await SitesOrderService.getOrderById(orderId);
      const invoiceId = Number(order?.invoiceId);
      const locationId = Number(order?.locationId);
      if (!invoiceId || !locationId || Number.isNaN(invoiceId) || Number.isNaN(locationId)) {
        console.warn('[AutoPrintOrder] Orden sin invoice/location para imprimir', {
          orderId,
          invoiceId: order?.invoiceId,
          locationId: order?.locationId
        });
        return;
      }
      console.info('[AutoPrintOrder] Orden resuelta', { orderId, invoiceId, locationId });

      const { viewPrint, autoPrint } = await getSitesOrdersPrintConfig(locationId);
      if (!viewPrint && !autoPrint) {
        console.info('[AutoPrintOrder] Impresión desactivada en settings', { orderId, locationId });
        return;
      }
      console.info('[AutoPrintOrder] Config impresión', { orderId, locationId, viewPrint, autoPrint });

      const ticketResponse = await fetchInvoiceTickets(invoiceId);
      const ticketPayload = ticketResponse?.data?.data || ticketResponse?.data || ticketResponse;
      const factura = ticketPayload?.factura || null;
      const deliveryTicket = ticketPayload?.deliveryTicket || null;
      const legacyExtraTickets = [
        ticketPayload?.welcomeTicket,
        ticketPayload?.loyaltyCardTicket,
        ticketPayload?.postSaleTicket
      ].filter(Boolean);
      const extraTickets = Array.isArray(ticketPayload?.extraTickets) && ticketPayload.extraTickets.length > 0
        ? ticketPayload.extraTickets.map(ticket => ticket?.content).filter(Boolean)
        : legacyExtraTickets;
      console.info('[AutoPrintOrder] Tickets obtenidos', {
        orderId,
        hasFactura: Boolean(factura),
        hasDeliveryTicket: Boolean(deliveryTicket),
        extraTickets: extraTickets.length
      });

      await printBase64(factura, viewPrint, autoPrint);
      await printBase64(deliveryTicket, viewPrint, autoPrint);
      for (const extraTicket of extraTickets) {
        await printBase64(extraTicket, viewPrint, autoPrint);
      }
      console.info('[AutoPrintOrder] Impresión completada', { orderId, invoiceId });
    } catch (error) {
      console.error('Error auto-imprimiendo tickets de orden web:', error);
    }
  }, [printBase64]);

  // Cargar notificaciones no leídas
  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [response, stockCountItems] = await Promise.all([
        SitesService.getNotifications(null, false, 1, 10),
        // Un fallo del aviso de conteo no tiene que tapar el resto de las notificaciones.
        SitesService.getStockCountNotifications().catch(() => [])
      ]);
      const notificationsList = response?.notifications || response || [];
      const stockCountNotifications = stockCountItems.map(stockCountToNotification);
      setNotifications([...stockCountNotifications, ...notificationsList]);

      // Contar no leídas. El conteo de stock suma 1 en total (no uno por sucursal ni por ítem a contar): la campanita avisa
      // que hay conteo pendiente, el detalle de cada sucursal está en la lista.
      const unread = notificationsList.filter(n => !n.isRead).length + (stockCountNotifications.length > 0 ? 1 : 0);

      // Reproducir sonido y refrescar pedidos si hay notificaciones nuevas (más no leídas que antes)
      if (!isFirstLoadRef.current && prevUnreadCountRef.current !== null && unread > prevUnreadCountRef.current) {
        void playNotificationSound();
      }

      // Este fetch (poll dinámico o apertura manual) puede ser el primero en enterarse de un
      // pedido web nuevo, antes que el polling de respaldo del modal de confirmación: avisarle
      // para que se muestre ya mismo en vez de esperar su propio ciclo.
      // Las solicitudes B2B de clientes (type "b2b") usan el mismo cartel.
      if (notificationsList.some((n) => (n?.type === 'order' || n?.type === 'b2b') && !n.isRead)) {
        window.dispatchEvent(new CustomEvent('notifications-new-orders'));
      }

      isFirstLoadRef.current = false;
      prevUnreadCountRef.current = unread;

      setUnreadCount(unread);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError(err.message);
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  }, []);

  // WebSocket: notificaciones en tiempo real; al recibir nueva orden, refrescar y avisar
  const handleNewNotification = useCallback(async (notificationData) => {
    const canHandle = await canHandleNotification(notificationData);
    if (!canHandle) return;

    if (notificationData?.type === 'order') {
      void playNotificationSound();
      window.dispatchEvent(new CustomEvent('notifications-new-orders'));
    } else if (notificationData?.type === 'b2b') {
      // El sonido de la solicitud B2B lo da el propio fetch (sube el contador de no leídas); acá solo se muestra el cartel.
      window.dispatchEvent(new CustomEvent('notifications-new-orders'));
    }
    fetchNotifications();
    autoPrintOrderTickets(notificationData);
  }, [fetchNotifications, autoPrintOrderTickets, canHandleNotification]);
  const { healthy: notificationsWsHealthy } = useNotificationsWebSocket(handleNewNotification);

  // Polling dinámico: 60s con WS saludable, 10s en reconexión/caída.
  useEffect(() => {
    fetchNotifications();
    const intervalMs = notificationsWsHealthy ? 60_000 : 10_000;
    const interval = setInterval(fetchNotifications, intervalMs);
    return () => clearInterval(interval);
  }, [fetchNotifications, notificationsWsHealthy]);

  useEffect(() => {
    const handleNotificationsRefresh = () => {
      fetchNotifications();
    };

    window.addEventListener('notifications-refresh', handleNotificationsRefresh);
    return () => window.removeEventListener('notifications-refresh', handleNotificationsRefresh);
  }, [fetchNotifications]);

  useEffect(() => {
    const handleAllRead = () => {
      // Actualización inmediata del badge
      setUnreadCount(0);
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      // Sync defensivo con backend
      fetchNotifications();
    };

    window.addEventListener('notifications-all-read', handleAllRead);
    return () => window.removeEventListener('notifications-all-read', handleAllRead);
  }, [fetchNotifications]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    fetchNotifications(); // Refrescar al abrir
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = async (notification) => {
    handleClose();
    if (notification.type === 'order' && !notification.isRead) {
      try {
        await SitesService.markNotificationAsRead(notification.id);
        setNotifications((items) => items.map((item) => (
          item.id === notification.id ? { ...item, isRead: true } : item
        )));
        setUnreadCount((count) => Math.max(0, count - 1));
        window.dispatchEvent(new CustomEvent('web-order-notification-read', { detail: { id: notification.id } }));
      } catch (requestError) {
        console.error('No se pudo marcar el pedido web como visto:', requestError);
      }
    }
    if (notification.type === 'stock_count') {
      navigate(`/stock-count/${notification.locationId}`);
      return;
    }
    if (notification.type === 'order') {
      navigate('/site/orders');
      return;
    }
    if (notification.type === 'expense_due') {
      navigate('/expenses');
      return;
    }
    if (notification.type === 'billing') {
      navigate('/notifications');
      return;
    }
    if (notification.type === 'b2b') {
      navigate('/b2b/proforma');
      return;
    }
    // Resto: redirigir a notificaciones (no marcar como leída aquí para que el usuario vea las nuevas)
    navigate('/site/notifications');
  };

  const handleViewAll = () => {
    handleClose();
    navigate('/notifications');
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'order':
        return 'warning';
      case 'contact':
        return 'primary';
      case 'career':
        return 'success';
      case 'newsletter':
        return 'info';
      case 'expense_due':
        return 'error';
      case 'billing':
        return 'error';
      case 'b2b':
        return 'secondary';
      case 'stock_count':
        return 'info';
      default:
        return 'default';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'order':
        return t('order') || 'Orden';
      case 'contact':
        return t('contact') || 'Contacto';
      case 'career':
        return t('career') || 'Carrera';
      case 'newsletter':
        return t('newsletter') || 'Newsletter';
      case 'expense_due':
        return 'Gasto';
      case 'billing':
        return t('billing') || 'Facturación';
      case 'b2b':
        return 'B2B';
      case 'stock_count':
        return 'Conteo';
      default:
        return type || '-';
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    return dayjs(dateString).fromNow();
  };

  return (
    <>
      <IconButton
        color="primary"
        onClick={handleClick}
        sx={{ position: 'relative' }}
      >
        <Badge badgeContent={unreadCount} color="error" max={99}>
          <ion-icon name="notifications-outline" style={{ fontSize: "28px" }}></ion-icon>
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 450,
            maxHeight: 600,
            mt: 1.5,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ p: 2.5, pb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            {t('notifications') || 'Notificaciones'}
          </Typography>
          {unreadCount > 0 && (
            <Typography variant="body2" color="text.secondary">
              {unreadCount} {t('unread') || 'no leídas'}
            </Typography>
          )}
        </Box>

        <Divider />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="error">
              {t('error_loading_notifications') || 'Error al cargar notificaciones'}
            </Typography>
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {t('no_notifications') || 'No hay notificaciones'}
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ maxHeight: 450, overflow: 'auto' }}>
              {notifications.map((notification) => (
                <MenuItem
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    py: 2,
                    px: 2.5,
                    backgroundColor: notification.isRead ? 'transparent' : 'rgba(25, 118, 210, 0.08)',
                    borderLeft: notification.isRead ? 'none' : '3px solid',
                    borderLeftColor: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'action.selected',
                    },
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: 'auto',
                    gap: 1.5,
                  }}
                >
                  <Chip
                    label={getTypeLabel(notification.type)}
                    size="small"
                    color={getTypeColor(notification.type)}
                    sx={{ height: 24, fontSize: '0.75rem', fontWeight: 500, flexShrink: 0 }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.25 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.primary',
                        fontWeight: 400,
                        width: '100%',
                        lineHeight: 1.4,
                        wordBreak: 'break-word',
                        fontSize: '0.875rem',
                      }}
                    >
                      {(() => {
                        const text = notification.description || notification.title || '-';
                        // Remover email del texto (formato: texto (email@ejemplo.com))
                        return text.replace(/\s*\([^@]+@[^)]+\)/g, '');
                      })()}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}
                    >
                      {formatTime(notification.createdAt)}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Box>
            <Divider />
            <Box sx={{ p: 1.5 }}>
              <Button
                fullWidth
                onClick={handleViewAll}
                sx={{ textTransform: 'none', py: 1 }}
              >
                {t('view_all_notifications') || 'Ver todas las notificaciones'}
              </Button>
            </Box>
          </>
        )}
      </Menu>
    </>
  );
};

export default NotificationsDropdown;
